import React from 'react';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaInstagram, FaRocket } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { primaryNavigation } from '../../seo/seoUtils';
import Cubesat from '../../assets/cubesat.png';
import './Footer.css';

const footerStats = [
  { value: '2018', label: 'Year founded' },
  { value: 'Design phase', label: 'Current program stage' },
  { value: '2028+', label: 'Target first flight' }
];

const socialLinks = [
  { icon: FaXTwitter, href: 'https://x.com/AntaralAero', label: 'X' },
  { icon: FaInstagram, href: 'https://www.instagram.com/antaralaerospace', label: 'Instagram' }
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const orbitStars = Array.from({ length: 22 }, (_, index) => ({
    id: index,
    style: {
      '--star-size': `${(index % 3) + 1}px`,
      '--star-top': `${6 + ((index * 11) % 72)}%`,
      '--star-left': `${4 + ((index * 17) % 92)}%`,
      '--star-delay': `${(index % 6) * 1.2}s`
    }
  }));

  return (
    <footer className="site-footer">
      <div className="site-footer__starfield" aria-hidden="true">
        {orbitStars.map((star) => (
          <span key={star.id} className="site-footer__star" style={star.style}></span>
        ))}
      </div>
      <div className="site-footer__nebula" aria-hidden="true"></div>

      <div className="site-footer__container">
        <div className="site-footer__top">
          <div className="site-footer__brand">
            <div className="site-footer__brand-mark">
              <FaRocket />
            </div>
            <div>
              <h3>Antaral Aerospace</h3>
              <p>
                Clean, capable, and launch-focused systems for the next generation of space companies.
              </p>
            </div>
          </div>

          <nav className="site-footer__nav" aria-label="Footer navigation">
            {primaryNavigation.map((item) => (
              <Link key={item.path} to={item.path} aria-label={item.schemaName} title={item.schemaName}>
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="site-footer__cta">
            <span className="site-footer__cta-label">Let&apos;s talk</span>
            <h4>Planning a mission, launch, or payload partnership?</h4>
            <a href="mailto:info@antaral.space" className="btn btn-primary">
              <FaEnvelope />
              info@antaral.space
            </a>
            <div className="site-footer__socials">
              {socialLinks.map((social) => {
                const IconComponent = social.icon;

                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                  >
                    <IconComponent />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="site-footer__stats">
          {footerStats.map((stat) => (
            <div key={stat.label} className="site-footer__stat">
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>

        <div className="site-footer__bottom">
          <p>&copy; {currentYear} Antaral Aerospace. All rights reserved.</p>
          <div className="site-footer__bottom-links">
            <Link to="/about">Privacy Policy</Link>
            <Link to="/missions">Launch Programs</Link>
            <Link to="/contact">Sitemap</Link>
          </div>
          <span>Built for startups shaping the future of orbital infrastructure.</span>
        </div>

        <div className="site-footer__scene" aria-hidden="true">
          <div className="site-footer__earth-atmosphere"></div>
          <svg
            className="site-footer__orbit-svg"
            viewBox="0 0 1440 360"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <defs>
              <filter id="footerSatelliteGlow" x="-60%" y="-60%" width="220%" height="220%">
                <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="rgba(112, 217, 255, 0.75)" />
                <feDropShadow dx="0" dy="0" stdDeviation="8" floodColor="rgba(34, 184, 240, 0.35)" />
              </filter>
            </defs>

            <image
              href={Cubesat}
              width="96"
              height="96"
              x="-48"
              y="-48"
              className="site-footer__orbit-satellite-svg"
              preserveAspectRatio="xMidYMid meet"
            >
              <animateMotion
                dur="14s"
                repeatCount="indefinite"
                rotate="auto"
                keyTimes="0; 0.5; 1"
                keyPoints="0; 0.5; 1"
                calcMode="linear"
                path="M -150 250 C 250 118 790 74 1590 244"
              />
            </image>
          </svg>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
